import { Trans, withI18n } from '@lingui/react'
import classNames from 'classnames'
import { arrayOf, bool, number, oneOf, shape } from 'prop-types'
import { compose } from 'redux'

import {
  AVAILABLE_PRESCRIPTION_TYPES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_PRIVAT,
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
  PRICE_TYPES,
} from 'shared/consts/productTemplates'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import {
  ListPrice,
  RetailPrice,
} from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantComponents'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  getListPriceSuperScript,
  isDiscountValid,
} from 'views/utils/productUtils'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import 'views/components/organisms/ProductVariants/ProductVariant/ProductVariant.scss'

const ProductVariantAvailableDetailsRebrand = ({
  discount,
  isMarketplaceProduct,
  prescriptionType,
  isMasterPage,
  lowestPrice,
  prices,
  publicRuntimeConfig: {
    publicConfig: { discountBoundaries, superScripts, locale },
    pageProperties: {
      product: { unitPricing, pharmaObl },
    },
  },
}) => {
  const isNoPrescriptionType =
    prescriptionType === PRESCRIPTION_TYPE_NO_PRESCRIPTION
  const isPrivateRecept = prescriptionType === PRESCRIPTION_TYPE_PRIVAT
  const isRetailPricePresent = prices.some(price => {
    return price.type === PRICE_TYPES.RETAIL
  })
  const isListPricePresent = prices.some(price => {
    return price.type === PRICE_TYPES.LIST
  })
  const listPriceAmount = getPriceForType(prices, PRICE_TYPES.LIST)
  const realPriceAmount = getPriceForType(
    prices,
    PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionType]
  )
  const isListPriceGreaterThanRealPrice =
    listPriceAmount?.amount > realPriceAmount.amount
  const showListPrice = !isNoPrescriptionType && isListPriceGreaterThanRealPrice

  const listPriceProps = {
    amount: isListPriceGreaterThanRealPrice ? listPriceAmount.amount : null,
    noLineThrough:
      (Boolean(listPriceAmount) &&
        Boolean(realPriceAmount) &&
        listPriceAmount.amount === realPriceAmount.amount) ||
      !isNoPrescriptionType,
  }
  const retailPriceProps = {
    amount: realPriceAmount.amount,
    prescriptionType,
  }

  const getBadge = ({
    discount,
    badgeLabel,
    color = 'red',
    showSupNotes = false,
    hasBonus = false,
  }) => (
    <DiscountBadge
      discount={discount}
      ellipse
      hasBonus={hasBonus}
      color={color}
      rounded
      smaller
      className="u-margin-small--right u-font-weight--bold"
      border
      showSupNotes={showSupNotes}
      fullwidth
    >
      <span suppressHydrationWarning className="u-no-wrap">
        {badgeLabel}
      </span>
    </DiscountBadge>
  )

  const getPriceBadgeBasedOdPrescriptionType = () => {
    switch (prescriptionType) {
      case PRESCRIPTION_TYPE_NO_PRESCRIPTION: {
        const showSupNotes = true
        const badgeLabel = `-${discount}%`

        return getBadge({ discount, badgeLabel, showSupNotes })
      }

      case PRESCRIPTION_TYPE_PRIVAT: {
        const privatePresctiptionPrice = prices.find(price => {
          return price.type === PRESCRIPTION_TYPE_PRIVAT
        })

        const currencySymbol = currencyToSymbol(
          privatePresctiptionPrice.currency
        )
        const bonus = privatePresctiptionPrice.bonus
        const bonusPriceString = formatPriceString({
          amount: bonus,
          locale,
          currencySymbol,
          symbolRight: false,
        })
        const color = 'rxgreen'
        const badgeLabel = (
          <div className="a-h3 u-font-weight--normal u-no-margin--bottom l-flex@medium">
            <Trans id="account.orders.overview.bonus" />:
            <div className="u-font-weight--bold u-padding-x-small--left@medium">
              {bonusPriceString}
            </div>
          </div>
        )
        const hasBonus = !!bonus
        const discount = bonus
        return bonus
          ? getBadge({ color, discount, badgeLabel, hasBonus })
          : null
      }

      default:
        return (
          <>
            <Trans id="general.label.additionalCharge" />:{' '}
          </>
        )
    }
  }

  const isValidDiscount = isDiscountValid(discount, discountBoundaries)

  const retailPriceWrapperClasses = classNames(
    'l-flex o-SearchProductListItem__prices__retail-price-wrapper',
    {
      'l-flex--vertical l-flex--start':
        !isNoPrescriptionType && !isPrivateRecept,
    }
  )
  const retailPriceClasses = classNames(
    'u-no-margin o-SearchProductListItem__prices__retail-price pdpPriceFont',
    {
      'u-color--red':
        !isMasterPage && (!isNoPrescriptionType || isValidDiscount),
    }
  )

  return (
    <div className="l-flex l-flex--distribute-space l-flex--end l-flex--wrap o-SearchProductListItem__content__buy-section u-padding u-padding-x-small--sides@small-tiny">
      <div className="u-margin--right">
        <div className={retailPriceWrapperClasses}>
          {!isMasterPage && getPriceBadgeBasedOdPrescriptionType()}
          {(isRetailPricePresent || isMasterPage) && (
            <p className={retailPriceClasses} data-qa-id="entry-price">
              <RetailPrice
                {...retailPriceProps}
                isMasterPage={isMasterPage}
                lowestPrice={lowestPrice}
              />
            </p>
          )}
        </div>
        {unitPricing && !isMasterPage && (
          <p className="u-no-margin u-margin-xx-small--bottom u-no-wrap u-color--grey">
            {unitPricing}
          </p>
        )}
        {(showListPrice || (isNoPrescriptionType && isValidDiscount)) &&
          isListPricePresent &&
          !isMasterPage && (
            <div className="l-flex u-no-margin o-SearchProductListItem__prices__list-price-wrapper">
              <div className="o-SearchProductListItem__prices__list-price__text">
                {isMarketplaceProduct ? (
                  <Trans id="general.label.miraklListPrice" />
                ) : (
                  <Trans id={'general.label.listPrice'} />
                )}
                <sup>
                  {getListPriceSuperScript({ superScripts, pharmaObl })}
                </sup>
                <span className="u-margin-xx-small--left o-SearchProductListItem__prices__list-price">
                  <ListPrice {...listPriceProps} />
                </span>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

ProductVariantAvailableDetailsRebrand.propTypes = {
  discount: number,
  isMarketplaceProduct: bool,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  prices: arrayOf(shape({})).isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  isMasterPage: bool,
  lowestPrice: number,
}

ProductVariantAvailableDetailsRebrand.defaultProps = {
  discount: 0,
  isMarketplaceProduct: false,
  isOutOfStock: false,
  isOutOfTrade: false,
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  isMasterPage: false,
}

export default compose(
  withShopAndRuntimeConfig,
  withI18n()
)(ProductVariantAvailableDetailsRebrand)
